import { ClassificationNodeSelectorV2Service } from '@/components/classification-node-selector-v2/classification-node-selector-v2.service';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import * as UsersStoreActions from '@/domain/users/store/users.actions';
import { FavoriteActivityService } from '@/features/homepage/services/favorite-activity/favorite-activity.service';
import { ConnectedAsChanged, ProjectChanged, UserConnected } from '@/libs/rosa/lib/core/services/auth/auth.events';
import { type CoreApiToken, RosaAuthService } from '@/rosa';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { ButtonStyle } from '@/ui/button/ui-button';
import { GlobalEventBusService } from '@/utils/event-bus/client/global-event-bus.service';
import { I18nService } from '@/utils/i18n/i18n.service';
import { parseURLParams } from '@/utils/toolkit/string/url.utils';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { filter, map, pairwise, switchMap, withLatestFrom } from 'rxjs/operators';
import type { ProjectId } from '../../interfaces/Project.interface';
import type { UserId } from '../../interfaces/User.interface';
import { AdvizeoModalService } from '../../services/advizeo-modal.service';
import { Page, RoutingService } from '../../services/routing/routing.service';
import type { AppState } from '../../store/app.state';
import { DashboardingStoreActions } from '../../store/dashboarding';
import { BuildingsStoreActions } from '../../store/entities/buildings';
import { BuildingsAccessesStoreActions } from '../../store/entities/buildings-accesses';
import { BusinessIndicatorsStoreActions } from '../../store/entities/business-indicators';
import { CategoriesStoreActions } from '../../store/entities/categories';
import { ContactsStoreActions } from '../../store/entities/contacts';
import { DashboardsStoreActions } from '../../store/entities/dashboards';
import { DeliveryPointsStoreActions } from '../../store/entities/delivery-points';
import { MetersStoreActions } from '../../store/entities/meters';
import { ProfilesStoreActions } from '../../store/entities/profiles';
import { ProjectsStoreActions } from '../../store/entities/projects';
import { RepartitionCategoriesStoreActions } from '../../store/entities/repartition-categories';
import { SensorsStoreActions } from '../../store/entities/sensors';
import { TailoredWidgetsStoreActions } from '../../store/entities/tailored-widgets';
import { TimeseriesTagsStoreActions } from '../../store/entities/timeseries-tag';
import { ProfileStoreActions } from '../../store/profile';
import { BrandingProvider } from '../branding/branding.provider';
import { BuildingsIndicatorsProvider } from '../buildings-indicators/buildings-indicators.provider';
import { BuildingsProvider } from '../buildings/buildings.provider';
import { BusinessIndicatorsProvider } from '../business-indicator/business-indicators.provider';
import { DashboardsProvider } from '../dashboards/dashboards.provider';
import { DeliveryPointProvider } from '../delivery-point/delivery-point.provider';
import { InformationProvider } from '../information/information.provider';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '../local-storage.contants';
import { MeterProvider } from '../meter/meter.provider';
import { ProfileProvider } from '../profile/profile.provider';
import { ProjectsProvider } from '../projects/projects.provider';
import { SensorsProvider } from '../sensors/sensors.provider';
import { StateStatusProvider } from '../state-status/state-status.provider';
import { TimeseriesTagProvider } from '../tag/timeseries-tag.provider';
import { TailoredWidgetsProvider } from '../tailored-widgets/tailored-widgets.provider';
import { TrackingService } from '../tracking/tracking.service';
import { UnitGroupsProvider } from '../unit-groups/unit-groups.provider';
import { UnitProvider } from '../unit/unit.provider';
import { WidgetProvider } from '../widget.provider';
import { DeveloperHelperService } from './../../services/developer-helper.service';

@Injectable({
    providedIn: 'root'
})
export class AuthProvider {
    private _state = inject<Store<AppState>>(Store);
    private _buildingsProvider = inject(BuildingsProvider);
    private _translate = inject(TranslateService);
    private _trackingService = inject(TrackingService);
    private _profileProvider = inject(ProfileProvider);
    private _widgetProvider = inject(WidgetProvider);
    private _projectsProvider = inject(ProjectsProvider);
    private _deliveryPointsProvider = inject(DeliveryPointProvider);
    private _routingService = inject(RoutingService);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _i18n = inject(I18nService);
    private _informationProvider = inject(InformationProvider);
    private _meterProvider = inject(MeterProvider);
    private _unitsProvider = inject(UnitProvider);
    private _unitGroupsProvider = inject(UnitGroupsProvider);
    private _buildingsIndicatorProvider = inject(BuildingsIndicatorsProvider);
    private _rosaAuthService = inject(RosaAuthService);
    private _brandingProvider = inject(BrandingProvider);
    private _sensorsProvider = inject(SensorsProvider);
    private _businessIndicatorsProvider = inject(BusinessIndicatorsProvider);
    private _dashboardsProvider = inject(DashboardsProvider);
    private _tailoredWidgetsProvider = inject(TailoredWidgetsProvider);
    private _eventBusService = inject(GlobalEventBusService);
    private _applicationModulesProvider = inject(ApplicationModulesService);
    private _timeseriesTagProvider = inject(TimeseriesTagProvider);
    private _stateStatusProvider = inject(StateStatusProvider);
    private _developerHelperService = inject(DeveloperHelperService);
    private _favoriteActivitiesService = inject(FavoriteActivityService);
    private _classificationNodeSelectorV2Service = inject(ClassificationNodeSelectorV2Service);

    private _currentProjectId: string | null = null;

    constructor() {
        this._projectsProvider.getUserCurrentProjectId$().subscribe(projectId => {
            if (projectId && projectId !== this._currentProjectId) {
                this._currentProjectId = projectId;
            }
        });

        this._projectsProvider.listenToProjectCreation$().subscribe(projectId => {
            this.changeProject(projectId);
        });
    }

    initializeAuthentication() {
        this.autologin()
            .then(() => {
                return Promise.all([
                    this._stateStatusProvider.waitForBuildingStateLoad(),
                    this._stateStatusProvider.waitForProfileStateLoad()
                ]);
            })
            .then(() => {
                const paramsFromRegister = parseURLParams(window.location.hash);
                if (
                    this._buildingsProvider.getBuildingsCount() > 0 &&
                    paramsFromRegister &&
                    paramsFromRegister['token'] &&
                    paramsFromRegister['token'][0] &&
                    paramsFromRegister['email'] &&
                    paramsFromRegister['email'][0]
                ) {
                    return this.proposeInvitation(paramsFromRegister['email']![0]!, paramsFromRegister['token']![0]!);
                }
            })
            .catch(err => {
                console.error(err);
            });

        // handle logout redirect
        this._rosaAuthService
            .isAuthenticated$()
            .pipe(
                pairwise(),
                filter(([wasAuthenticated, isAuthenticated]) => wasAuthenticated && !isAuthenticated),
                withLatestFrom(this._brandingProvider.getBranding$()),
                map(([[wasAuthenticated, isAuthenticated], branding]) => ({
                    wasAuthenticated,
                    isAuthenticated,
                    idpLogoutUrl: branding?.idpLogoutUrl
                }))
            )
            .subscribe(({ idpLogoutUrl }) => {
                if (idpLogoutUrl) {
                    window.location.href = idpLogoutUrl;
                } else {
                    this._routingService.resetNavigationTo(Page.LOGIN, {});
                }
            });
    }

    login(email: string, password: string): Promise<void> {
        return this._rosaAuthService.login(email, password).then(token => {
            return this.postLogin(token);
        });
    }

    logout() {
        this.unregisterToServices();
        this.clearLocalStorage();
        this.resetAllData();
        this._rosaAuthService.logout();
    }

    autologin(): Promise<void> {
        return this._rosaAuthService.autologin(this.promptForReloginAs.bind(this)).then(token => {
            return this.postLogin(token);
        });
    }

    async loginAs(email: string, projectId?: string): Promise<void> {
        const token = await this._rosaAuthService.loginAs(email, projectId);

        const userId = token.username;
        projectId = token.project_id;
        this.resetUserData();
        this._eventBusService.publish(new UserConnected());
        this._eventBusService.publish(new ConnectedAsChanged());

        await Promise.all([this.fetchUserData(userId, token.project_id), this.fetchProjectData(projectId)]);
    }

    backToMainAccount(): Promise<void> {
        this.resetUserData();
        this._eventBusService.publish(new ConnectedAsChanged());
        return this._rosaAuthService.backToMainAccount().then(token => {
            return this.postLogin(token);
        });
    }

    changeProject(projectId: ProjectId): Promise<void> {
        this._state.dispatch(new ProjectsStoreActions.SetIsChanging({ isChanging: true }));
        return this._rosaAuthService
            .changeProject(projectId)
            .then(token => {
                this.resetProjectData();
                this._eventBusService.publish(new ProjectChanged({ newProjectId: projectId }));
                this.clearProjectSessionStorage();
                return this.fetchProjectData(token.project_id);
            })
            .then(() => {
                this._state.dispatch(new ProjectsStoreActions.SetIsChanging({ isChanging: false }));
                this._routingService.resetNavigationToHomePage();
            })
            .catch(err => {
                this._state.dispatch(new ProjectsStoreActions.SetIsChanging({ isChanging: false }));
                throw err;
            });
    }

    registerUserConnection(userId: UserId): Promise<void> {
        return this._profileProvider.updateProfileUser(
            {
                lastConnection: moment().toISOString(true)
            },
            userId
        );
    }

    async postLogin(token: CoreApiToken): Promise<void> {
        const userId = token.username;
        const projectId = token.project_id;
        this.registerUserConnection(userId);
        this._eventBusService.publish(new UserConnected());

        await Promise.all([
            this.fetchAdvizeoData(),
            this.fetchUserData(userId, projectId).then(() => {
                return this.registerToServices();
            }),
            this.fetchProjectData(projectId)
        ]);
    }

    fetchUserData(userId: UserId, selectedProjectId: string): Promise<void> {
        return this._profileProvider
            .fetchCurrentUser(userId)
            .then(() => {
                return Promise.all([this._projectsProvider.fetchUserProjects(selectedProjectId)]);
            })
            .then(() => {})
            .catch(err => console.log('Error when loading current user', err));
    }

    fetchProjectData(projectId: string): Promise<void> {
        this._projectsProvider.setUserCurrentProjectId(projectId);
        return Promise.all([
            this._profileProvider.fetchRights().then((rights: string[]) => this._widgetProvider.getDashboardsList(rights)),
            this._dashboardsProvider.fetchDashboards(),
            this._tailoredWidgetsProvider.fetchTailoredWidgets(),
            this._buildingsProvider.initializeProjectIdBuildingData(projectId),
            this._applicationModulesProvider.fetchApplicationModules(projectId),
            this._favoriteActivitiesService.fetchAllFavoriteActivitiesForStateInitialization()
        ])
            .catch(err => {
                console.error('Error while fetching project Data', err);
            })
            .then(() => {
                this.preFetchAsyncProjectData();
            });
    }

    async fetchAdvizeoData(): Promise<void> {
        this._developerHelperService.fetchDataSourcesReference();

        await Promise.all([
            this._meterProvider.fetchAllUniverseMeterTypes(),
            this._unitsProvider.fetchAllReferenceUnits(),
            this._unitGroupsProvider.fetchAllReferenceUnitGroups(),
            this._buildingsIndicatorProvider.fetchBuildingsIndicators()
        ]);
    }

    /**
     * Prefetch data that is not required to accelerate some page load later
     * A delay is set before starting the background fetch to avoid cramping the original load
     */
    preFetchAsyncProjectData(): void {
        setTimeout(() => {
            this._sensorsProvider.initializeSensorsStateIfNeeded();
            this._timeseriesTagProvider.initializeTimeseriesTagsStateIfNeeded();
            this._deliveryPointsProvider.initializeDeliveryPointStateIfNeeded();
        }, 1000);
    }

    clearLocalStorage() {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_NODE_ID); // To remove/change after menu-accordion refactor
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.SELECTED_DASHBOARD);
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.SELECTED_PERIOD);
    }

    clearProjectSessionStorage() {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.SELECTED_DASHBOARD);
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.SELECTED_PERIOD);
    }

    registerToServices() {
        this._trackingService.init();
        this._trackingService.sessionStart();
        this._trackingService.identifyUser();
    }

    unregisterToServices() {
        this._trackingService.sessionEnd().catch(err => {
            console.error('Error while ending tracking session', err);
        });
    }

    promptForReloginAs(email: string): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            this._i18n.waitForTranslationsLoad().then(() => {
                this._advizeoModalService.openDialog({
                    title: this._translate.instant('POPOVERS.RELOGIN_AS.TITLE'),
                    message: this._translate.instant('POPOVERS.RELOGIN_AS.MESSAGE', { email }),
                    buttons: [
                        {
                            text: this._translate.instant('STUFF.NO'),
                            color: SharedUiColorNameV2.PRIMARY,
                            buttonStyle: ButtonStyle.OUTLINE,
                            onClick: () => {
                                localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_NODE_ID);
                                resolve(false);
                            }
                        },
                        {
                            text: this._translate.instant('STUFF.YES'),
                            color: SharedUiColorNameV2.PRIMARY,
                            onClick: () => {
                                resolve(true);
                            }
                        }
                    ]
                });
            });
        });
    }

    resetPassword(email: string): Promise<void> {
        this._informationProvider.showToast('POPOVERS.FORGOT_PASSWORD.REQUEST_ON_GOING', 'info');

        return firstValueFrom(
            this._brandingProvider.getBranding$().pipe(
                switchMap(branding => {
                    return this._rosaAuthService.resetPassword(email, branding.rootUri);
                })
            )
        )
            .then(() => {
                this._informationProvider.showToast('POPOVERS.FORGOT_PASSWORD.MAIL_SENT', 'success');
            })
            .catch(() => {
                this._informationProvider.showToast(this._translate.instant('POPOVERS.FORGOT_PASSWORD.CANT_FIND_MAIL', { email }), 'error');
            });
    }

    backToMainAccountAndRedirectToMainPage() {
        this.backToMainAccount().then(() => {
            this._routingService
                .resetNavigationTo(Page.CONNECTED_AS, {}, { skipLocationChange: true }) // needed to have a refresh of the homepage.
                .then(() => this._routingService.resetNavigationToHomePage());
        });
    }

    resetAllData() {
        this.resetUserData();
    }

    resetUserData() {
        this._state.dispatch(new ProjectsStoreActions.Reset());
        this._state.dispatch(new ProfileStoreActions.Reset());
        this.resetProjectData();
    }

    resetProjectData() {
        this._state.dispatch(new ProjectsStoreActions.SetCurrentProjectId({ projectId: null! }));
        this._state.dispatch(new DashboardingStoreActions.Reset());
        this._state.dispatch(new DeliveryPointsStoreActions.Reset());
        this._state.dispatch(new ProfileStoreActions.ResetRights());
        this._state.dispatch(new ContactsStoreActions.Reset());
        this._state.dispatch(new MetersStoreActions.Reset());
        this._state.dispatch(new SensorsStoreActions.Reset());
        this._state.dispatch(new CategoriesStoreActions.Reset());
        this._state.dispatch(new ProfilesStoreActions.Reset());
        this._state.dispatch(new BuildingsStoreActions.Reset());
        this._state.dispatch(new BuildingsAccessesStoreActions.Reset());
        this._state.dispatch(new BusinessIndicatorsStoreActions.Reset());
        this._state.dispatch(new DashboardsStoreActions.Reset());
        this._state.dispatch(new TailoredWidgetsStoreActions.Reset());
        this._state.dispatch(new TimeseriesTagsStoreActions.Reset());
        this._state.dispatch(new RepartitionCategoriesStoreActions.Reset());
        this._state.dispatch(new UsersStoreActions.Reset());
        this._classificationNodeSelectorV2Service.resetToDefault();
        this._businessIndicatorsProvider.notifyProjectChange();
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_NODE_ID); // To remove/change after menu-accordion refactor
    }

    private proposeInvitation(email: string, token: string): Promise<void> {
        if (email === this._profileProvider.getUserEmail()) {
            return this._profileProvider.acceptInvitation(email, token).then();
        } else {
            return new Promise<string>(resolve => {
                this._advizeoModalService.openDialog({
                    title: this._translate.instant('POPOVERS.INVITATION.TITLE'),
                    message: this._translate.instant('POPOVERS.INVITATION.MESSAGE'),
                    buttons: [
                        {
                            text: this._translate.instant('POPOVERS.INVITATION.BUTTON_ACCEPT_INVITATION'),
                            onClick: () => {
                                resolve('ACCEPT_INVITATION');
                            },
                            color: SharedUiColorNameV2.PRIMARY
                        },
                        {
                            text: this._translate.instant('POPOVERS.INVITATION.BUTTON_OTHER_ACCOUNT'),
                            onClick: () => {
                                resolve('OTHER_ACCOUNT');
                            },
                            color: SharedUiColorNameV2.PRIMARY
                        },
                        {
                            text: this._translate.instant('POPOVERS.INVITATION.BUTTON_CANCEL'),
                            onClick: () => {
                                resolve('CANCEL');
                            },
                            color: SharedUiColorNameV2.PRIMARY,
                            buttonStyle: ButtonStyle.OUTLINE
                        }
                    ]
                });
            }).then((choice: string) => {
                switch (choice) {
                    case 'ACCEPT_INVITATION':
                        return this._profileProvider.acceptInvitation(email, token).then();
                    case 'OTHER_ACCOUNT':
                        this.logout();
                        document.location.replace(window.location.origin + '/#/register?token=' + token + '&email=' + email);
                    case 'CANCEL':
                    default:
                }
            });
        }
    }
}
